import React, { useRef, useState, useEffect, useCallback } from 'react';
import Editor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import font from 'suneditor/src/plugins/submenu/font';
import fontSize from 'suneditor/src/plugins/submenu/fontSize';
import formatBlock from 'suneditor/src/plugins/submenu/formatBlock';
import fontColor from 'suneditor/src/plugins/submenu/fontColor';
import hiliteColor from 'suneditor/src/plugins/submenu/hiliteColor';
import align from 'suneditor/src/plugins/submenu/align';
import horizontalRule from 'suneditor/src/plugins/submenu/horizontalRule';
import list from 'suneditor/src/plugins/submenu/list';
import lineHeight from 'suneditor/src/plugins/submenu/lineHeight';
import table from 'suneditor/src/plugins/submenu/table';
import link from 'suneditor/src/plugins/dialog/link';
import image from 'suneditor/src/plugins/dialog/image';
import template from 'suneditor/src/plugins/submenu/template';
import Resolution from './Plugins/Resolution';
import ActionItem from './Plugins/ActionItem';
import AgendaPlaceholder from './Plugins/AgendaPlaceholder';
import MeetingTitle from './Plugins/MeetingTitle';
import MeetingCreator from './Plugins/MeetingCreator';
import MeetingGroup from './Plugins/MeetingGroup';
import MeetingDate from './Plugins/MeetingDate';
import MeetingVenue from './Plugins/MeetingVenue';
import './WYSIWYG.css';
import { Box, Button, CircularProgress, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import MinutesEditor from './MinutesEditor';

const tabKey = '&nbsp;&nbsp;&nbsp;&nbsp;';

export interface AttendanceInterface {
    present: Array<any>,
    absent: Array<any>,
    apologised: Array<any>,
}

export interface WYSIWYGProps {
    module: string,
    content: string,
    contentRef?: React.Ref<string | null>,
    onSave?: (contents: string, isChanged: boolean) => void,
    height?: string,
    attendance?: AttendanceInterface
    minutesTemplate?: any,
    changeHandler?: (content: string) => any,
    mode?: 'classic' | 'inline' | 'balloon' | 'balloon-always',
    agenda?: Array<any>,
    meetingId?: number | string,
    meetingTitle?: string | null,
    meetingDate?: string | null,
    meetingTown?: string,
    meetingRoom?: string
    meetingBuilding?: string,
    meetingCreator?: string,
    meetingGroup?: string | number,
    meetingVenue?: string,
    mutableAgenda?: string
    adoptedAgenda?: Array<any>
    fetchedActionItems?: any[] | null
    setFetchedActionItems?: React.Dispatch<React.SetStateAction<any[] | null | undefined>> | undefined
}

// Audio format options
const AUDIO_FORMATS = [
    { value: 'audio/webm', label: 'WebM' },
    { value: 'audio/mp4', label: 'MP4' },
    { value: 'audio/wav', label: 'WAV' }
];

// Speech recognition language options
const SPEECH_LANGUAGES = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' }
];

const WYSIWYG = (props: WYSIWYGProps) => {

    const handleClick = (e: MouseEvent) => {
    }
    const handleScroll = (e: Event) => {
    }

    const { module, meetingId } = props;
    const [isSpeaking, setIsSpeaking] = useState(false);
    const contentRef = useRef<string>(props.content);
    const editorRef = useRef<any>(null);

    // Audio recording state
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
    const [audioURL, setAudioURL] = useState<string | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [selectedAudioFormat, setSelectedAudioFormat] = useState('audio/webm');

    // Audio recording refs for proper cleanup
    const audioChunksRef = useRef<Blob[]>([]);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Speech-to-text (transcription) state
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isTranscriptionPaused, setIsTranscriptionPaused] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('en-US');
    const [isProcessing, setIsProcessing] = useState(false);
    const recognitionRef = useRef<any>(null);
    const [interimTranscript, setInterimTranscript] = useState('');

    // Error handling state
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Cleanup function
    const cleanup = useCallback(() => {
        // Cleanup audio URL
        if (audioURL) {
            URL.revokeObjectURL(audioURL);
        }
        // Cleanup media stream
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        // Cleanup recording timer
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        // Cleanup speech recognition
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
    }, [audioURL]);

    // Cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    // Recording timer effect
    useEffect(() => {
        if (isRecording && !isPaused) {
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
        }
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, [isRecording, isPaused]);

    // Format recording duration
    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper to get plain text from HTML
    const getPlainText = (html: string) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
    };

    // Get cursor position in editor
    const getCursorPosition = () => {
        try {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                return selection.getRangeAt(0);
            }
        } catch (error) {
            console.warn('Could not get cursor position:', error);
        }
        return null;
    };

    // Update contentRef on editor change
    const handleContentChange = (content: string) => {
        contentRef.current = content;
        if (typeof props.changeHandler === "function") {
            props.changeHandler(content);
        }
    };

    // Enhanced error handling
    const handleError = (message: string, error?: any) => {
        console.error(message, error);
        setError(message);
        setIsProcessing(false);
        setIsRecording(false);
        setIsTranscribing(false);
    };

    const showSuccess = (message: string) => {
        setSuccess(message);
    };

    // Check browser support
    const checkBrowserSupport = () => {
        const hasMediaDevices = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        const hasSpeechRecognition = !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

        return { hasMediaDevices, hasSpeechRecognition };
    };

    const handleStartRecording = async () => {
        try {
            const { hasMediaDevices } = checkBrowserSupport();
            if (!hasMediaDevices) {
                throw new Error("Audio recording not supported in this browser.");
            }

            setIsProcessing(true);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });

            mediaStreamRef.current = stream;

            // Check if the selected format is supported
            let mimeType = selectedAudioFormat;
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'audio/webm'; // Fallback
                setSelectedAudioFormat(mimeType);
            }

            const recorder = new MediaRecorder(stream, { mimeType });
            setMediaRecorder(recorder);

            // Reset chunks
            audioChunksRef.current = [];
            setAudioChunks([]);
            setRecordingDuration(0);

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                    setAudioChunks(prev => [...prev, e.data]);
                }
            };

            recorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                const url = URL.createObjectURL(audioBlob);

                // Cleanup previous URL
                if (audioURL) {
                    URL.revokeObjectURL(audioURL);
                }

                setAudioURL(url);
                setIsRecording(false);
                setIsPaused(false);
                showSuccess('Recording completed successfully!');
            };

            recorder.onerror = (e) => {
                handleError('Recording failed', e);
            };

            recorder.start(1000); // Collect data every second
            setIsRecording(true);
            setIsProcessing(false);
            showSuccess('Recording started');

        } catch (error: any) {
            handleError(`Failed to start recording: ${error.message}`, error);
        }
    };

    const handlePauseRecording = () => {
        if (mediaRecorder && isRecording && !isPaused) {
            mediaRecorder.pause();
            setIsPaused(true);
            showSuccess('Recording paused');
        }
    };

    const handleResumeRecording = () => {
        if (mediaRecorder && isRecording && isPaused) {
            mediaRecorder.resume();
            setIsPaused(false);
            showSuccess('Recording resumed');
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();

            // Stop all tracks
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
                mediaStreamRef.current = null;
            }
        }
    };

    // Insert transcribed text at cursor position or at the end
    const insertTranscribedText = (text: string) => {
        try {
            // For MinutesEditor, use its specific method if available
            if (module === 'MEETING_MINUTES_TAKING' && editorRef.current) {
                // Try to insert at cursor position
                editorRef.current.insertHTML(`<p>${text}</p>`);
                return;
            }

            // For other editors, append to content
            if (typeof props.changeHandler === "function") {
                const current = contentRef.current || "";
                const cursorRange = getCursorPosition();

                if (cursorRange) {
                    // Insert at cursor position (more complex implementation needed)
                    props.changeHandler(current + `<p>${text}</p>`);
                } else {
                    // Append at the end
                    props.changeHandler(current + `<p>${text}</p>`);
                }
            }
        } catch (error) {
            console.warn('Failed to insert text at cursor, appending at end:', error);
            // Fallback to appending
            if (typeof props.changeHandler === "function") {
                const current = contentRef.current || "";
                props.changeHandler(current + `<p>${text}</p>`);
            }
        }
    };

    const handleStartTranscription = () => {
        try {
            const { hasSpeechRecognition } = checkBrowserSupport();
            if (!hasSpeechRecognition) {
                throw new Error("Speech recognition is not supported in this browser.");
            }

            const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            const recognition = new SpeechRecognition();

            recognition.lang = selectedLanguage;
            recognition.interimResults = true;
            recognition.maxAlternatives = 1;
            recognition.continuous = true;

            recognition.onstart = () => {
                setIsTranscribing(true);
                setIsTranscriptionPaused(false);
                setInterimTranscript('');
                showSuccess('Transcription started - speak now');
            };

            recognition.onend = () => {
                if (isTranscribing && !isTranscriptionPaused) {
                    // Restart if not manually stopped
                    try {
                        recognition.start();
                    } catch (error) {
                        setIsTranscribing(false);
                    }
                } else {
                    setIsTranscribing(false);
                }
            };

            recognition.onerror = (event: any) => {
                let errorMessage = 'Speech recognition error';
                switch (event.error) {
                    case 'no-speech':
                        errorMessage = 'No speech detected. Please try again.';
                        break;
                    case 'audio-capture':
                        errorMessage = 'No microphone access. Please check permissions.';
                        break;
                    case 'not-allowed':
                        errorMessage = 'Microphone access denied. Please enable microphone permissions.';
                        break;
                    case 'network':
                        errorMessage = 'Network error during speech recognition.';
                        break;
                    default:
                        errorMessage = `Speech recognition error: ${event.error}`;
                }
                handleError(errorMessage);
            };

            recognition.onresult = (event: any) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                setInterimTranscript(interimTranscript);

                if (finalTranscript) {
                    insertTranscribedText(finalTranscript.trim());
                    showSuccess('Text transcribed and inserted');
                }
            };

            recognitionRef.current = recognition;
            recognition.start();

        } catch (error: any) {
            handleError(`Failed to start transcription: ${error.message}`, error);
        }
    };

    const handlePauseTranscription = () => {
        if (recognitionRef.current && isTranscribing) {
            recognitionRef.current.stop();
            setIsTranscriptionPaused(true);
            showSuccess('Transcription paused');
        }
    };

    const handleResumeTranscription = () => {
        if (isTranscriptionPaused) {
            handleStartTranscription();
        }
    };

    const handleStopTranscription = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
            setIsTranscribing(false);
            setIsTranscriptionPaused(false);
            setInterimTranscript('');
            showSuccess('Transcription stopped');
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle shortcuts when in MEETING_MINUTES_TAKING module
            if (module !== 'MEETING_MINUTES_TAKING') return;

            if (event.ctrlKey || event.metaKey) {
                switch (event.key) {
                    case 'r':
                        event.preventDefault();
                        if (isRecording) {
                            handleStopRecording();
                        } else {
                            handleStartRecording();
                        }
                        break;
                    case 't':
                        event.preventDefault();
                        if (isTranscribing) {
                            handleStopTranscription();
                        } else {
                            handleStartTranscription();
                        }
                        break;
                    case 'p':
                        event.preventDefault();
                        if (isRecording && !isPaused) {
                            handlePauseRecording();
                        } else if (isRecording && isPaused) {
                            handleResumeRecording();
                        } else if (isTranscribing && !isTranscriptionPaused) {
                            handlePauseTranscription();
                        } else if (isTranscriptionPaused) {
                            handleResumeTranscription();
                        }
                        break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [module, isRecording, isPaused, isTranscribing, isTranscriptionPaused]);

    const CustomEditor = React.useCallback(() => {
        switch (module) {
            case 'AGENDA_TEMPLATE':
                const agnendaTemplateButtonList = [
                    ['undo', 'redo'],
                    [':p-More Paragraph-default.more_paragraph', 'font', 'fontSize', 'formatBlock'],
                    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
                    ['fontColor', 'hiliteColor'],
                    ['removeFormat'],
                    ['outdent', 'indent'],
                    ['align', 'horizontalRule', 'list', 'lineHeight'],
                    ['fullScreen', 'preview'],
                    ['-right', 'save'],
                    ['-right', ':r-More Rich-default.more_plus',
                        'table',
                        'image',
                        'link',
                        {
                            name: 'AgendaPlaceholder',
                            dataCommand: 'AgendaPlaceholder',
                            buttonClass: '',
                            title: 'Agenda placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="icon ti-agenda plugin-icon"></i>'
                        },
                        {
                            name: 'MeetingTitle',
                            dataCommand: 'MeetingTitle',
                            buttonClass: '',
                            title: 'Meeting title placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="icon ti-text plugin-icon"></i>'
                        },
                        {
                            name: 'MeetingDate',
                            dataCommand: 'MeetingDate',
                            buttonClass: '',
                            title: 'Meeting date placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="icon ti-calendar plugin-icon"></i>'
                        },
                        {
                            name: 'MeetingVenue',
                            dataCommand: 'MeetingVenue',
                            buttonClass: '',
                            title: 'Meeting venue placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="icon ti-direction-alt plugin-icon"></i>'
                        },
                        {
                            name: 'MeetingCreator',
                            dataCommand: 'MeetingCreator',
                            buttonClass: '',
                            title: 'Meeting Creator placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="fa fa-user plugin-icon"></i>'
                        },
                        {
                            name: 'MeetingGroup',
                            dataCommand: 'MeetingGroup',
                            buttonClass: '',
                            title: 'Meeting Group placeholder',
                            dataDisplay: 'submenu',
                            innerHTML: '<i class="fa fa-users plugin-icon"></i>'
                        },
                    ],
                ];
                return <Editor
                    ref={editorRef}
                    lang="en"
                    setOptions={{
                        mode: props.mode,
                        height: props.height ?? '600px',
                        // @ts-ignore
                        plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, image, AgendaPlaceholder, MeetingTitle, MeetingDate, MeetingVenue, MeetingGroup, MeetingCreator],
                        buttonList: agnendaTemplateButtonList,
                        showPathLabel: false,
                        charCounter: true,
                        callBackSave: props.onSave
                    }}
                    setContents={props.content}
                    onChange={handleContentChange}
                />;
            case 'MINUTES_TEMPLATE':
                const minutesTemplateButtonList = [["undo", "redo", "font", "fontSize", "formatBlock", "bold", "underline", "italic", "fontColor", "hiliteColor", "removeFormat", "list", "outdent", "indent", "align", "horizontalRule", "lineHeight", "table", "image", "fullScreen", {
                    name: 'MeetingTitle',
                    dataCommand: 'MeetingTitle',
                    buttonClass: '',
                    title: 'Meeting title placeholder',
                    dataDisplay: 'submenu',
                    innerHTML: '<i class="icon ti-text plugin-icon"></i>'
                },
                    {
                        name: 'MeetingDate',
                        dataCommand: 'MeetingDate',
                        buttonClass: '',
                        title: 'Meeting date placeholder',
                        dataDisplay: 'submenu',
                        innerHTML: '<i class="icon ti-calendar plugin-icon"></i>'
                    },
                    {
                        name: 'MeetingVenue',
                        dataCommand: 'MeetingVenue',
                        buttonClass: '',
                        title: 'Meeting venue placeholder',
                        dataDisplay: 'submenu',
                        innerHTML: '<i class="icon ti-direction-alt plugin-icon"></i>'
                    }, "save"]];
                return <Editor
                    ref={editorRef}
                    lang="en"
                    setOptions={{
                        mode: props.mode,
                        height: props.height ?? '600px',
                        // @ts-ignore
                        plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, image, MeetingTitle, MeetingDate, MeetingVenue],
                        buttonList: minutesTemplateButtonList,
                        showPathLabel: false,
                        charCounter: true,
                        callBackSave: props.onSave
                    }}
                    setContents={props.content}
                    onChange={handleContentChange}
                />;
            case 'AGENDA_CREATION':
                let agendaItems = '<h2><u>AGENDA</u></h2>';
                let WYSIWYG_content;

                Array.isArray(props.agenda) && props.agenda.map((agenda: any, i) => {
                    agendaItems += `<h2>${(i + 1).toString().concat('. ', agenda.text)}</h2>`;

                    if (agenda.children) {
                        agenda.children.map((child: { text: string, children: Array<any> }, j: number) => {
                            agendaItems += `<h3>${tabKey}${(i + 1).toString().concat('.', (j + 1).toString(), '. ', child.text)}</h3>`;

                            if (child.children) {
                                child.children.map((grandchild, k) => {
                                    agendaItems += `<h4>${tabKey}${tabKey}${(i + 1).toString().concat('.', (j + 1).toString(), '.', (k + 1).toString(), '. ', grandchild.text)}</h4>`;
                                    return k++;
                                });
                            }
                            return j++;
                        });
                    }
                    return i++;
                });

                if (props.meetingTitle && props.meetingDate && props.meetingTown && props.meetingRoom && props.meetingBuilding && props.meetingCreator && props.meetingGroup && props.meetingVenue) {
                    WYSIWYG_content = props.content.replace('::Meeting Title::', props.meetingTitle.toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Creator::', props.meetingCreator.toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Group::', props.meetingGroup.toString().toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Town::', props.meetingTown.toString().toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Building::', props.meetingBuilding.toString().toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Room::', props.meetingRoom.toString().toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Date::', props.meetingDate.toString().toUpperCase());
                    WYSIWYG_content = WYSIWYG_content.replace('::Meeting Venue::', props.meetingVenue.toString().toUpperCase());

                    WYSIWYG_content = WYSIWYG_content.includes('::Meeting Agenda::') ? WYSIWYG_content.replace('::Meeting Agenda::', agendaItems) : WYSIWYG_content.concat('<div style="text-align: left;">', agendaItems, '</div>');
                }
                const agendaCreationButtons = [["undo", "redo"], [':p-More Paragraph-default.more_paragraph', 'font', 'fontSize', 'formatBlock'], [':t-More Text-default.more_text', 'bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'fontColor', 'hiliteColor', 'removeFormat'], ['outdent', 'indent', 'align', 'horizontalRule', 'list', 'lineHeight'], ["table", "link", "image"], ["preview", "fullScreen"], ["save"]];

                return <Editor
                    // ref={editorRef}
                    lang="en"
                    setOptions={{
                        mode: props.mode,
                        height: props.height ?? '540px',
                        plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, link, image],
                        buttonList: agendaCreationButtons,
                        showPathLabel: false,
                        charCounter: true,
                        callBackSave: props.onSave
                    }}
                    setContents={props.content ? WYSIWYG_content : agendaItems}
                />;
            case 'AGENDA_MUTATION':
                let _agendaItems = '<h2><u>AGENDA</u></h2>';
                Array.isArray(props.agenda) && props.agenda.map((agenda: any, i: number) => {

                    _agendaItems += `<h2>${(i + 1).toString().concat('. ', agenda.text)}</h2>`;

                    if (agenda.children) {
                        agenda.children.map((child: { text: string, children: Array<any> }, j: number) => {
                            _agendaItems += `<h3>${tabKey}${(i + 1).toString().concat('.', (j + 1).toString(), '. ', child.text)}</h3>`;

                            if (child.children) {
                                child.children.map((grandchild: any, k: number) => {
                                    _agendaItems += `${tabKey}${tabKey}<h4>${tabKey}${tabKey}${(i + 1).toString().concat('.', (j + 1).toString(), '.', (k + 1).toString(), '. ', grandchild.text)}</h4>`;
                                    return k++;
                                });
                            }
                            return j++;
                        });
                    }
                    return i++;
                });

                const agendaMutationButtons = [["undo", "redo"], [':p-More Paragraph-default.more_paragraph', 'font', 'fontSize', 'formatBlock'], [':t-More Text-default.more_text', 'bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'fontColor', 'hiliteColor', 'removeFormat'], ['outdent', 'indent', 'align', 'horizontalRule', 'list', 'lineHeight'], ["table", "link", "image"], ["preview", "fullScreen"], ["save"]];
                return <Editor
                    //ref={editorRef}
                    lang="en" setOptions={
                        {
                            mode: props.mode,
                            height: props.height ?? '540px',
                            plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, link, image],
                            buttonList: agendaMutationButtons,
                            showPathLabel: false,
                            charCounter: true,
                            callBackSave: props.onSave
                        }
                    } setContents={props.mutableAgenda ?? _agendaItems} />;
            case 'MEETING_MINUTES_TAKING':
                const minutesTakingButtonList = [["undo", "redo"], [':p-More Paragraph-default.more_paragraph', 'font', 'fontSize', 'formatBlock'], [':t-More Text-default.more_text', 'bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'fontColor', 'hiliteColor', 'removeFormat'], ['outdent', 'indent', 'align', 'horizontalRule', 'list', 'lineHeight'], ["table", "link", "image"], ["preview", "fullScreen"], [{
                    name: 'Resolution',
                    dataCommand: 'Resolution',
                    buttonClass: '',
                    // HTML title attribute
                    title: 'Resolution',
                    // 'submenu' or 'dialog' or '' (command button)
                    dataDisplay: 'submenu',
                    // HTML to be append to button
                    innerHTML: '<i class="icon ti-comment-alt plugin-icon"></i>'
                },
                {
                    name: 'ActionItem',
                    dataCommand: 'ActionItem',
                    buttonClass: '',
                    // HTML title attribute
                    title: 'Action point',
                    // 'submenu' or 'dialog' or '' (command button)
                    dataDisplay: 'submenu',
                    // HTML to be append to button
                    innerHTML: '<i class="icon ti-flag-alt-2 plugin-icon"></i>'
                }],
                ["save"]];

                // const minutesTakingButtonList = [["undo", "redo", "font", "fontSize", "formatBlock", "bold", "underline", "italic", "superscript", "fontColor", "hiliteColor", "removeFormat", "outdent", "indent", "align", "horizontalRule", "list", "lineHeight", "table", "link", "image", "preview", "print", "template","save", {
                //     name: 'Resolution',
                //     dataCommand: 'Resolution',
                //     buttonClass: '',
                //     // HTML title attribute
                //     title: 'Resolution',
                //     // 'submenu' or 'dialog' or '' (command button)
                //     dataDisplay: 'submenu',
                //     // HTML to be append to button
                //     innerHTML: '<i class="icon ti-comment-alt plugin-icon"></i>'
                // },
                // {
                //     name: 'ActionItem',
                //     dataCommand: 'ActionItem',
                //     buttonClass: '',
                //     // HTML title attribute
                //     title: 'Action point',
                //     // 'submenu' or 'dialog' or '' (command button)
                //     dataDisplay: 'submenu',
                //     // HTML to be append to button
                //     innerHTML: '<i class="icon ti-flag-alt-2 plugin-icon"></i>'
                // }
                // ]];
                return (
                    <MinutesEditor
                        handleClick={handleClick}
                        handleScroll={handleScroll}
                        parent_props={props}
                        meetingId={meetingId}
                        fetchedActionItems={props.fetchedActionItems}
                        setFetchedActionItems={props.setFetchedActionItems}
                        minutesTakingButtonList={minutesTakingButtonList}
                    />
                );
            case 'MEETING_MINUTES_MUTATION':
                const minutesMutationButtonList = [["undo", "redo"], [':p-More Paragraph-default.more_paragraph', 'font', 'fontSize', 'formatBlock'], [':t-More Text-default.more_text', 'bold', 'underline', 'italic', 'strike', 'subscript', 'superscript', 'fontColor', 'hiliteColor', 'removeFormat'], ['outdent', 'indent', 'align', 'horizontalRule', 'list', 'lineHeight'], ["table", "link", "image"], ["preview", "fullScreen"], [{
                    name: 'Resolution',
                    dataCommand: 'Resolution',
                    buttonClass: '',
                    // HTML title attribute
                    title: 'Resolution',
                    // 'submenu' or 'dialog' or '' (command button)
                    dataDisplay: 'submenu',
                    // HTML to be append to button
                    innerHTML: '<i class="icon ti-comment-alt plugin-icon"></i>'
                },
                {
                    name: 'ActionItem',
                    dataCommand: 'ActionItem',
                    buttonClass: '',
                    // HTML title attribute
                    title: 'Action point',
                    // 'submenu' or 'dialog' or '' (command button)
                    dataDisplay: 'submenu',
                    // HTML to be append to button
                    innerHTML: '<i class="icon ti-flag-alt-2 plugin-icon"></i>'
                }], ["save"]];
                return <Editor lang="en" setOptions={
                    {
                        mode: props.mode,
                        height: props.height ?? '65vh',
                        plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, link, image, Resolution, ActionItem],
                        buttonList: minutesMutationButtonList,
                        showPathLabel: false,
                        charCounter: true,
                        callBackSave: (content: string, isChanged: boolean) => { typeof props.onSave === "function" && props.onSave(content, isChanged) }

                    }
                } setContents={props.content} onChange={handleContentChange} />;
            default:
                const defaultButtonList = [["undo", "redo", "font", "fontSize", "formatBlock", "bold", "underline", "italic", "superscript", "fontColor", "hiliteColor", "removeFormat", "outdent", "indent", "align", "horizontalRule", "list", "lineHeight", "table", "link", "image", "preview", "print", "template", "fullScreen", "save", {
                    name: 'Resolution',
                    dataCommand: 'Resolution',
                    buttonClass: '',
                    // HTML title attribute
                    title: 'Resolution',
                    // 'submenu' or 'dialog' or '' (command button)
                    dataDisplay: 'submenu',
                    // HTML to be append to button
                    innerHTML: '<i class="icon ti-comment-alt plugin-icon"></i>'
                },
                    {
                        name: 'ActionItem',
                        dataCommand: 'ActionItem',
                        buttonClass: '',
                        // HTML title attribute
                        title: 'Action item',
                        // 'submenu' or 'dialog' or '' (command button)
                        dataDisplay: 'submenu',
                        // HTML to be append to button
                        innerHTML: '<i class="icon ti-flag-alt-2 plugin-icon"></i>'
                    },
                    {
                        name: 'AgendaPlaceholder',
                        dataCommand: 'AgendaPlaceholder',
                        buttonClass: '',
                        // HTML title attribute
                        title: 'Insert agenda placeholder',
                        // 'submenu' or 'dialog' or '' (command button)
                        dataDisplay: 'submenu',
                        // HTML to be append to button
                        innerHTML: '<i class="icon ti-agenda plugin-icon"></i>'
                    }]];
                return <Editor lang="en" setOptions={
                    {
                        mode: props.mode,
                        height: props.height ?? '540px',
                        //@ts-ignore
                        plugins: [font, fontSize, formatBlock, fontColor, hiliteColor, align, horizontalRule, list, lineHeight, table, link, image, template, Resolution, ActionItem, AgendaPlaceholder],
                        buttonList: defaultButtonList,
                        showPathLabel: false,
                        charCounter: true,
                        callBackSave: props.onSave
                    }
                } setContents={props.content} onChange={handleContentChange} />;
        }
    }, [module, props, meetingId])
    return (
        <Box height='100%' width='100%' position='relative' overflow='hidden'>
            <div style={{ marginBottom: 8 }}>
                {/* Removed Read Aloud (text-to-speech) buttons */}
                {/* Only show audio recording in MEETING_MINUTES_TAKING */}
                {module === 'MEETING_MINUTES_TAKING' && (
                    <>
                        {/* <button onClick={handleStartRecording} disabled={isRecording} style={{ marginLeft: 8 }}>
                            🎤 Record
                        </button>
                        <button onClick={handleStopRecording} disabled={!isRecording} style={{ marginLeft: 8 }}>
                            ⏹ Stop Recording
                        </button>
                        {audioURL && (
                            <audio controls src={audioURL} style={{ marginLeft: 8 }} />
                        )} */}
                    </>
                )}
                {/* Only show transcription in MEETING_MINUTES_TAKING */}
                {props.module === 'MEETING_MINUTES_TAKING' && (
                    <>
                    </>
                )}
            </div>
            {CustomEditor()}
        </Box>
    );
}
export default WYSIWYG;